import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Field } from '../../components/ui/Field';
import { Icon } from '../../components/ui/Icon';
import { Card, ErrorNote } from '../../components/ui/misc';
import { ApiError } from '../../lib/api/errors';
import { useAuth } from './useAuth';

interface LocationState {
  from?: { pathname: string };
}

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as LocationState | null)?.from?.pathname ?? '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<{ message: string; tone: 'alert' | 'accent' } | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const outcome = await login(email.trim(), password);
      if (outcome.mfaRequired) {
        navigate('/login/mfa', {
          replace: true,
          state: { mfaChallengeToken: outcome.mfaChallengeToken, from, email: email.trim() },
        });
        return;
      }
      navigate(from, { replace: true });
    } catch (err) {
      setError(messageFor(err));
      setBusy(false);
    }
  }

  return (
    <AuthLayout badge="Kenyan Workforce, Biometrics & Statutory Engine">
      <h1 className="text-xl font-semibold text-ink">Sign in</h1>
      <p className="mt-1 text-sm text-ink-muted">Mervonn Workforce · System of Record</p>
      <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-4">
        {error && <ErrorNote tone={error.tone}>{error.message}</ErrorNote>}
        <Field
          label="Email"
          type="email"
          autoComplete="username"
          required
          icon={<Icon name="mail" className="text-[18px]" />}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Field
          label="Password"
          type={showPassword ? 'text' : 'password'}
          autoComplete="current-password"
          required
          icon={<Icon name="key" className="text-[18px]" />}
          labelExtra={
            <Link to="/forgot-password" className="text-xs font-medium text-primary hover:underline">
              Forgot password?
            </Link>
          }
          endAdornment={
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              // Deliberately doesn't contain the word "password": Playwright's
              // `getByLabel` matches any element's accessible name by
              // case-insensitive substring, so an aria-label containing
              // "password" here collides with `getByLabel('Password')` on
              // the field itself (found running the real e2e-style check
              // for this phase — would have broken `auth.spec.ts` too).
              aria-label={showPassword ? 'Hide typed characters' : 'Show typed characters'}
              className="px-2.5 text-ink-subtle transition-colors hover:text-ink"
            >
              <Icon name={showPassword ? 'visibility_off' : 'visibility'} className="text-[18px]" />
            </button>
          }
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <Button type="submit" loading={busy} className="mt-1 w-full">
          <span>Sign in</span>
          <Icon name="arrow_forward" className="text-[18px]" />
        </Button>
      </form>
      <div className="mt-6 flex items-center justify-between border-t border-sunken pt-4 text-[11px] text-ink-muted">
        <span className="flex items-center gap-1.5 font-medium">
          <Icon name="verified" className="text-[15px] text-positive" />
          Kenya Data Protection Act (2019)
        </span>
        <span className="font-mono">TLS encrypted</span>
      </div>
    </AuthLayout>
  );
}

export function AuthLayout({ children, badge }: { children: React.ReactNode; badge?: string }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas px-4 py-10">
      <Card className="w-full max-w-sm p-6 md:p-8">
        <div className="mb-6 flex flex-col items-center text-center">
          <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-md border border-primary bg-primary text-sm font-bold tracking-tight text-primary-fg shadow-card">
            HC
          </div>
          <span className="text-lg font-semibold tracking-tight text-ink">HR Clock</span>
          {badge && (
            <div className="mt-2.5 inline-flex items-center gap-1.5 rounded-full border border-line bg-canvas px-2.5 py-0.5 text-[11px] text-ink-muted">
              <Icon name="verified_user" className="text-[13px] text-positive" />
              {badge}
            </div>
          )}
        </div>
        {children}
      </Card>
    </div>
  );
}

function messageFor(err: unknown): { message: string; tone: 'alert' | 'accent' } {
  if (err instanceof ApiError) {
    if (err.status === 401 || err.code === 'UNAUTHENTICATED') {
      return { message: 'That email and password did not match. Check them and try again.', tone: 'alert' };
    }
    if (err.code === 'RATE_LIMITED') {
      return { message: 'Too many attempts. Wait a minute and try again.', tone: 'accent' };
    }
    return { message: err.message, tone: 'alert' };
  }
  return { message: 'Could not reach the server. Check your connection and try again.', tone: 'alert' };
}
